import { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Textarea } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  RootState,
  updateAgentMessageState,
  useAppDispatch,
  useUpdateAgentFeedbackMutation,
  getFeedbackDraft,
  saveFeedbackDraft,
  clearFeedbackDraft,
} from '@circuitry-ai/doc-data';
import { CtyButton } from '../button/CtyButton';
import { CtyShowToast, checkType } from '../toast/CtyToast';

export type ClaimIntakeHeaderFeedbackProps = Readonly<{
  /** aiworker_usage_id — sent as `message_id` on agent usage feedback PATCH. */
  messageId: string;
}>;

/** Agent usage feedback — PATCH aiworkers/usage/feedback; guest auth via claim-intake REST in doc-data. */
export function ClaimIntakeHeaderFeedback({ messageId }: ClaimIntakeHeaderFeedbackProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [updateAgentFeedback, { isLoading }] = useUpdateAgentFeedbackMutation();
  const { agentMessageState } = useSelector((state: RootState) => state.chatAdvisorSliceReducer);
  const agentState = agentMessageState?.[messageId];
  const savedFeedback = agentState?.feedback ?? '';
  const commentHistory = agentState?.comments ?? [];

  const [localFeedback, setLocalFeedback] = useState(savedFeedback);
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentText, setCommentText] = useState('');

  useEffect(() => {
    if (!isLoading) setLocalFeedback(savedFeedback);
  }, [savedFeedback, isLoading]);

  const submitFeedback = useCallback(
    async (feedback: string, prev: string, comment?: string) => {
      setLocalFeedback(feedback);
      try {
        const response = await updateAgentFeedback({
          feedback,
          message_id: messageId,
          ...(comment?.trim() ? { comment: comment.trim() } : {}),
        }).unwrap();
        dispatch(
          updateAgentMessageState({
            messageId,
            updates: { feedback },
          })
        );
        CtyShowToast(checkType.success, response?.message ?? t('Feedback updated successfully'));
      } catch (e) {
        setLocalFeedback(prev);
        CtyShowToast(checkType.error, (e as Error)?.message ?? t('Something went wrong'));
      }
    },
    [dispatch, messageId, t, updateAgentFeedback]
  );

  const onLikeDislike = useCallback(
    (kind: 'like' | 'dislike') => {
      const isActive = localFeedback === kind;
      const next = kind === 'like' ? (isActive ? 'remove_like' : 'like') : isActive ? 'remove_dislike' : 'dislike';
      void submitFeedback(next, localFeedback);
    },
    [localFeedback, submitFeedback]
  );

  const closeCommentModal = useCallback(() => {
    saveFeedbackDraft(messageId, commentText);
    setCommentOpen(false);
  }, [commentText, messageId]);

  const onSaveComment = useCallback(async () => {
    const text = commentText.trim();
    if (!text) return;
    try {
      const response = await updateAgentFeedback({
        feedback: 'comment',
        message_id: messageId,
        comment: text,
      }).unwrap();
      dispatch(
        updateAgentMessageState({
          messageId,
          updates: {
            comment: {
              comment: text,
              feedback_type: 'comment',
            },
          },
        })
      );
      CtyShowToast(checkType.success, response?.message ?? t('Comment submitted successfully'));
      clearFeedbackDraft(messageId);
      setCommentText('');
      setCommentOpen(false);
    } catch (e) {
      saveFeedbackDraft(messageId, text);
      CtyShowToast(checkType.error, (e as Error)?.message ?? t('Something went wrong'));
    }
  }, [commentText, dispatch, messageId, t, updateAgentFeedback]);

  const hasComments = commentHistory.length > 0;

  return (
    <>
      <div className="w-fit flex rounded-full bg-content1 chat-feedback-container border px-1">
        <FeedbackIconButton
          tooltip={t('Like')}
          id={`claim-intake-like-${messageId}`}
          active={localFeedback === 'like'}
          activeColor="#22c55e"
          filledIcon="gravity-ui:thumbs-up-fill"
          emptyIcon="iconamoon:like-thin"
          onPress={() => onLikeDislike('like')}
          disabled={isLoading}
        />
        <FeedbackIconButton
          tooltip={t('Dislike')}
          id={`claim-intake-dislike-${messageId}`}
          active={localFeedback === 'dislike'}
          activeColor="#ef4444"
          filledIcon="gravity-ui:thumbs-down-fill"
          emptyIcon="mynaui:dislike"
          onPress={() => onLikeDislike('dislike')}
          disabled={isLoading}
        />
        <CtyButton
          tooltipContent={t('Comment')}
          toolTipColor="warning"
          id={`claim-intake-comment-${messageId}`}
          isIconOnly
          radius="full"
          size="sm"
          variant="light"
          onPress={() => {
            setCommentText(getFeedbackDraft(messageId));
            setCommentOpen(true);
          }}
        >
          <Icon
            icon={hasComments ? 'fluent:comment-24-filled' : 'fluent:comment-24-regular'}
            width={16}
            height={16}
            style={hasComments ? { color: '#0070f0' } : undefined}
          />
        </CtyButton>
      </div>

      <Modal isOpen={commentOpen} onClose={closeCommentModal} placement="center">
        <ModalContent>
          <ModalHeader>{t('Add a comment')}</ModalHeader>
          <ModalBody>
            <Textarea
              minRows={4}
              placeholder={t('Add comment')}
              value={commentText}
              onValueChange={setCommentText}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="bordered" onPress={closeCommentModal}>
              {t('Cancel')}
            </Button>
            <Button color="primary" isDisabled={!commentText.trim() || isLoading} onPress={() => void onSaveComment()}>
              {t('Save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

function FeedbackIconButton(props: Readonly<{
  tooltip: string;
  id: string;
  active: boolean;
  activeColor: string;
  filledIcon: string;
  emptyIcon: string;
  onPress: () => void;
  disabled?: boolean;
}>) {
  const { tooltip, id, active, activeColor, filledIcon, emptyIcon, onPress, disabled } = props;
  return (
    <CtyButton
      tooltipContent={tooltip}
      toolTipColor="warning"
      id={id}
      isIconOnly
      radius="full"
      size="sm"
      variant="light"
      onPress={onPress}
      isDisabled={disabled}
    >
      <Icon
        icon={active ? filledIcon : emptyIcon}
        width={16}
        height={16}
        style={active ? { color: activeColor } : undefined}
      />
    </CtyButton>
  );
}
